import { API_BASE } from "../config";
// =============================================================================
// File: frontend/src/components/HealistScoreDisplay.jsx
// Unified Healist Score — 8-Component Display with XAI + Healist Age
// Patent claims exercised: P-003 Claims 4-8,15-17, P-001 Claims 4,7,8
// =============================================================================

import React, { useState, useEffect } from 'react';
import MedicalDisclaimer from './MedicalDisclaimer';
import XaiPromptPanel from './XaiPromptPanel';
import { CitationsButton } from './Citations';
import { citationCount } from '../utils/xaiHelpers';

const COMPONENT_META = {
  activity:             { icon: '\u{1F3C3}', label: 'Activity' },
  nutrition_adherence:  { icon: '\u{1F957}', label: 'Nutrition' },
  food_quality:         { icon: '\u{1F966}', label: 'Food Quality' },
  hydration:            { icon: '\u{1F4A7}', label: 'Hydration' },
  sleep:                { icon: '\u{1F634}', label: 'Sleep' },
  diet_compliance:      { icon: '\u{1F3AF}', label: 'Diet Compliance' },
  body_vitals:          { icon: '\u{2764}\u{FE0F}', label: 'Body & Vitals' },
  objectives:           { icon: '\u{2B50}', label: 'Objectives' },
};

const HealistScoreDisplay = ({
  token,
  fetchWithAuth,
  compact = false,
  showRecommendations = true,
  onRefresh
}) => {
  const authFetch = fetchWithAuth || ((url, opts) => fetch(url, { ...opts, headers: { ...opts?.headers, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }));
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showXai, setShowXai] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState(null);

  useEffect(() => {
    loadScore();
  }, []);

  const loadScore = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await authFetch(`${API_BASE}/api/profile/healist-score`);

      if (!response.ok) {
        if (response.status === 404) {
          setScoreData(null);
          return;
        }
        throw new Error('Failed to load Healist Score');
      }

      const data = await response.json();
      setScoreData(data);
    } catch (err) {
      console.error('Error loading score:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    if (historyData) {
      setShowHistory(!showHistory);
      return;
    }
    try {
      const response = await authFetch(`${API_BASE}/api/profile/healist-score/history?days=7`);
      if (response.ok) {
        const data = await response.json();
        setHistoryData(data.entries || []);
        setShowHistory(true);
      }
    } catch (err) {
      console.error('Error loading score history:', err);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'var(--color-success, #059669)';
    if (score >= 60) return 'var(--brand-primary, #1E3A5F)';
    if (score >= 40) return 'var(--color-warning, #F59E0B)';
    if (score >= 20) return '#F97316';
    return 'var(--color-error, #EF4444)';
  };

  const getGradeInfo = (grade) => {
    const grades = {
      'A': { color: 'var(--color-success, #059669)', label: 'Excellent' },
      'B': { color: 'var(--brand-primary, #1E3A5F)', label: 'Good' },
      'C': { color: 'var(--color-warning, #F59E0B)', label: 'Average' },
      'D': { color: '#F97316', label: 'Below Average' },
      'F': { color: 'var(--color-error, #EF4444)', label: 'Needs Improvement' }
    };
    return grades[grade] || grades['C'];
  };

  if (loading) {
    return (
      <div className={compact ? 'hsd-container hsd-container--compact' : 'hsd-container'}>
        <div className="hsd-loading">Loading your Healist Score...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={compact ? 'hsd-container hsd-container--compact' : 'hsd-container'}>
        <div className="hsd-error">{error}</div>
      </div>
    );
  }

  if (!scoreData || !scoreData.has_data) {
    return (
      <div className={compact ? 'hsd-container hsd-container--compact' : 'hsd-container'}>
        <div className="hsd-noData">
          <div className="hsd-noDataIcon">{'\u{1F4CA}'}</div>
          <h4 className="hsd-noDataTitle">No Healist Score Yet</h4>
          <p className="hsd-noDataMessage">
            Start logging meals, water intake, and activity to calculate your personalized health score!
          </p>
        </div>
      </div>
    );
  }

  const { total_score, grade, change, components, recommendations, xai_data, healist_age } = scoreData;
  const gradeInfo = getGradeInfo(grade);
  const scoreColor = getScoreColor(total_score);

  // SVG circle
  const size = compact ? 120 : 160;
  const strokeWidth = compact ? 10 : 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (total_score / 100) * circumference;

  // Max height for sparkline bars
  const maxHistoryScore = historyData ? Math.max(...historyData.map(d => d.total_score), 1) : 100;

  return (
    <div className={compact ? 'hsd-container hsd-container--compact' : 'hsd-container'}>
      <div className="hsd-header">
        <h3 className="hsd-title">Your Healist Score</h3>
        <button className="hsd-refreshButton" onClick={loadScore}>Refresh</button>
      </div>

      {/* Score Circle */}
      <div className="hsd-scoreCircle">
        <svg className="hsd-scoreRing" viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke={scoreColor} strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={circumference - progress}
            strokeLinecap="round"
            className="hsd-scoreRingProgress"
          />
        </svg>
        <div className="hsd-scoreValue">
          <div className="hsd-scoreNumber" style={{ color: scoreColor }}>
            {Math.round(total_score)}
          </div>
          <div className="hsd-scoreGrade" style={{ color: gradeInfo.color }}>
            Grade {grade}
          </div>
          <div className="hsd-gradeLabel">{gradeInfo.label}</div>
        </div>
      </div>

      {/* Change indicator + Grade badge */}
      <div className="hsd-metaRow">
        {change && (
          <span className={`hsd-changeIndicator ${change.startsWith('+') ? 'hsd-changeIndicator--positive' : 'hsd-changeIndicator--negative'}`}>
            {change}
          </span>
        )}
      </div>

      {/* Healist Age */}
      {!compact && healist_age && (
        <div className="hsd-healistAge">
          <div className="hsd-healistAgeLabel">Healist Age</div>
          {healist_age.needs_dob ? (
            <div className="hsd-healistAgePrompt">
              Set your date of birth in <strong>Body Characteristics</strong> to see your Healist Age
            </div>
          ) : (
            <>
              <div className="hsd-healistAgeValue">
                <span className="hsd-healistAgeNumber" style={{ color: healist_age.difference >= 0 ? 'var(--color-success, #059669)' : 'var(--color-error, #EF4444)' }}>
                  {healist_age.healist_age}
                </span>
                <span className="hsd-healistAgeDiff">
                  {healist_age.difference > 0 ? `${healist_age.difference} years younger` :
                   healist_age.difference < 0 ? `${Math.abs(healist_age.difference)} years older` :
                   'Matches actual age'}
                </span>
              </div>
              <div className="hsd-healistAgeActual">
                Actual age: {healist_age.chronological_age}
              </div>
            </>
          )}
        </div>
      )}

      {/* 8-Component Grid */}
      {!compact && components && (
        <div className="hsd-componentsGrid">
          {Object.entries(components).map(([key, comp]) => {
            const meta = COMPONENT_META[key] || { icon: '\u{1F4CA}', label: key };
            return (
              <div key={key} className="hsd-componentCard">
                <div className="hsd-componentHeader">
                  <span>{meta.icon}</span>
                  <span className="hsd-componentLabel">{meta.label}</span>
                </div>
                <div className="hsd-componentValue">
                  {comp.data_available ? (
                    <span className="hsd-componentScore" style={{ color: getScoreColor(comp.score) }}>
                      {Math.round(comp.score)}
                    </span>
                  ) : (
                    <span className="hsd-noDataBadge">No data</span>
                  )}
                  <span className="hsd-componentWeight">{comp.weight}%</span>
                </div>
                <div className="hsd-progressBar">
                  <div
                    className="hsd-progressFill"
                    style={{ width: `${comp.score}%`, backgroundColor: getScoreColor(comp.score) }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* History Sparkline */}
      {!compact && (
        <div className="hsd-historySection">
          <button className="hsd-historyToggle" onClick={loadHistory}>
            {showHistory ? 'Hide 7-Day Trend' : 'Show 7-Day Trend'}
          </button>
          {showHistory && historyData && (
            <div className="hsd-historyBars">
              {historyData.map((entry, idx) => (
                <div key={idx} className="hsd-historyBarWrapper">
                  <div
                    className="hsd-historyBar"
                    style={{
                      height: `${(entry.total_score / maxHistoryScore) * 100}%`,
                      backgroundColor: getScoreColor(entry.total_score)
                    }}
                    title={`${entry.date}: ${Math.round(entry.total_score)}`}
                  />
                  <span className="hsd-historyBarLabel">
                    {new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* XAI Toggle */}
      {!compact && xai_data && (
        <>
          <CitationsButton
            open={showXai}
            count={citationCount(xai_data, 'Healist Score')}
            onToggle={() => setShowXai(!showXai)}
          />
          {showXai && <XaiPromptPanel promptData={xai_data} featureName="Healist Score" />}
        </>
      )}

      {/* Recommendations */}
      {showRecommendations && !compact && recommendations && recommendations.length > 0 && (
        <div className="hsd-recommendations">
          <h4 className="hsd-recommendationTitle">Improvement Tips</h4>
          {recommendations.slice(0, 3).map((rec, idx) => {
            const priorityColors = {
              'high': 'var(--color-error, #EF4444)',
              'medium': 'var(--color-warning, #F59E0B)',
              'low': 'var(--brand-primary, #1E3A5F)'
            };
            return (
              <div
                key={idx}
                className="hsd-recommendationCard"
                style={{ borderLeftColor: priorityColors[rec.priority] || 'var(--color-info)' }}
              >
                <div className="hsd-recommendationArea">
                  {rec.icon} {rec.area.replace(/_/g, ' ').toUpperCase()}
                </div>
                <div className="hsd-recommendationMessage">{rec.message}</div>
                {rec.tip && (
                  <div className="hsd-recommendationTip">{rec.tip}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <MedicalDisclaimer variant="health" />
    </div>
  );
};

export default HealistScoreDisplay;
